/**
 * O ponteiro da demonstração guiada.
 *
 * É o mesmo gesto do agente (`src/agente.ts`, `src/astrea.ts`): uma seta que
 * anda até o que está sendo explicado, para ninguém ficar olhando a tela mudar
 * sozinha sem saber quem mexeu. Aqui ele é DOM puro, e não componente — a seta
 * aponta para elementos que OUTROS componentes desenharam, e um nó fora da
 * árvore deles não obriga a mesa inteira a re-renderizar a cada passo.
 *
 * A diferença que importa em relação ao original: este ponteiro NÃO clica. Lá
 * ele chamava `el.click()` para preencher um formulário de teste; na mesa o
 * mesmo gesto em cima de "Enviar recado" manda mensagem para o cliente de
 * verdade. Aqui ele só aponta, e quem troca de tela é a URL — que é leitura.
 *
 * O realce também é um nó nosso, posto POR CIMA do alvo, em vez de uma classe
 * no elemento da mesa. Assim sair no meio não pode deixar contorno grudado num
 * campo: some o nó, some o realce.
 */

const ID = "ponteiro-demo";
const ID_REALCE = "realce-demo";

/* A paleta da mesa, no lugar do --agente-1/2 do original. */
const TINTA = "var(--color-terra)";
const REALCE = "var(--color-verde-fundo)";

export const esperar = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms));

/**
 * Quem pediu menos movimento no sistema.
 *
 * No original isto só calava um pulso decorativo. Aqui o movimento é a
 * funcionalidade inteira, então a resposta é a seta SALTAR de um alvo para o
 * outro em vez de deslizar — continua mostrando quem mexeu, sem varrer a tela.
 */
export const semAnimacao = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

type Caixa = { left: number; top: number; width: number; height: number };
type Janela = { largura: number; altura: number };

/**
 * Onde a seta pousa para apontar um alvo.
 *
 * Está separada da pintura porque é a única parte que é conta: a seta para no
 * começo do alvo (no meio de um botão de 400px ela apontaria para o vazio do
 * meio dele) e nunca passa da borda da janela — alvo meio fora da vista deixa a
 * seta na beirada, dizendo de que lado ele está, em vez de sumir junto.
 */
export function onde(caixa: Caixa, janela: Janela) {
  const alvoX = caixa.left + Math.min(caixa.width / 2, 90);
  return {
    x: Math.round(Math.min(Math.max(alvoX, 16), janela.largura - 16)),
    y: Math.round(Math.min(Math.max(caixa.top + caixa.height / 2, 16), janela.altura - 16)),
    /* Encostado na direita o rótulo cresceria para fora da tela: vira de lado. */
    espelhado: alvoX > janela.largura - 210,
  };
}

const seta = () => document.getElementById(ID);
const anel = () => document.getElementById(ID_REALCE);

/** Monta a seta e o realce. Idempotente: o StrictMode monta duas vezes. */
export function montar() {
  if (seta()) return;

  const p = document.createElement("div");
  p.id = ID;
  /* Decoração: quem usa leitor de tela recebe o texto do passo pelo HUD, que é
     `role="status"`. Duas vozes dizendo a mesma coisa é uma de mais. */
  p.setAttribute("aria-hidden", "true");
  p.style.cssText =
    "position:fixed;left:0;top:0;z-index:106;pointer-events:none;opacity:0;transform:translate(50vw,50vh);" +
    (semAnimacao()
      ? "transition:opacity var(--t-media)"
      : "transition:transform .5s cubic-bezier(.33,.85,.28,1),opacity var(--t-media)");
  p.innerHTML =
    '<svg width="24" height="24" viewBox="0 0 22 22" fill="none" style="display:block;filter:drop-shadow(0 3px 6px rgba(21,23,26,.45))">' +
    `<path d="M4 2.5 17.5 9.8l-5.9 1.4-2.3 5.7z" fill="${TINTA}" stroke="var(--color-papel)" stroke-width="1.4" stroke-linejoin="round"/>` +
    "</svg>" +
    `<span style="position:absolute;top:17px;background:${TINTA};color:var(--color-creme);font-size:10px;font-weight:700;letter-spacing:.02em;padding:3px 8px;border-radius:99px;white-space:nowrap;max-width:min(60vw,240px);overflow:hidden;text-overflow:ellipsis;box-shadow:0 4px 12px rgba(21,23,26,.3)"></span>`;
  document.body.appendChild(p);

  const r = document.createElement("div");
  r.id = ID_REALCE;
  r.setAttribute("aria-hidden", "true");
  r.style.cssText =
    `position:fixed;left:0;top:0;width:0;height:0;z-index:104;pointer-events:none;opacity:0;border:2px solid ${REALCE};border-radius:12px;box-shadow:0 0 0 5px rgba(176,74,38,.12);` +
    /* A posição do anel anda por `transform`, como a da seta: com `left`/`top`
       animados o navegador refazia o layout da página a cada quadro do passo —
       e o anel é um retângulo por cima de tudo, que não precisava disso. Sobram
       largura e altura, que mudam de alvo para alvo e não têm equivalente. */
    (semAnimacao()
      ? "transition:opacity var(--t-media)"
      : "transition:transform .4s cubic-bezier(.33,.85,.28,1),width .4s cubic-bezier(.33,.85,.28,1),height .4s cubic-bezier(.33,.85,.28,1),opacity var(--t-media)");
  document.body.appendChild(r);
}

/** Tira os dois nós da tela. É a limpeza inteira: não há classe para desfazer. */
export function desmontar() {
  seta()?.remove();
  anel()?.remove();
}

/**
 * Aponta um elemento e escreve o rótulo ao lado da seta.
 *
 * Sem elemento (o passo aponta algo que esta largura de tela não mostra) a seta
 * se recolhe em vez de apontar o canto: melhor nenhuma seta que uma seta
 * mentindo.
 */
export function apontar(el: Element | null, rotulo: string) {
  const p = seta();
  const r = anel();
  if (!p || !r) return;
  const caixa = el?.getBoundingClientRect();
  if (!caixa || (!caixa.width && !caixa.height)) return esconder();

  const pos = onde(caixa, { largura: window.innerWidth, altura: window.innerHeight });
  p.style.opacity = "1";
  p.style.transform = `translate(${pos.x}px,${pos.y}px)`;
  const balao = p.querySelector("span");
  if (balao instanceof HTMLElement) {
    balao.textContent = rotulo;
    /* `textContent`, nunca `innerHTML`: o rótulo é texto do roteiro e não tem
       por que poder virar marcação. */
    balao.style.left = pos.espelhado ? "auto" : "19px";
    balao.style.right = pos.espelhado ? "10px" : "auto";
  }

  r.style.opacity = "1";
  r.style.transform = `translate(${Math.round(caixa.left - 4)}px,${Math.round(caixa.top - 4)}px)`;
  r.style.width = `${Math.round(caixa.width + 8)}px`;
  r.style.height = `${Math.round(caixa.height + 8)}px`;
}

/** Recolhe a seta sem desmontar — passo que é só explicação não tem alvo. */
export function esconder() {
  const p = seta();
  const r = anel();
  if (p) p.style.opacity = "0";
  if (r) r.style.opacity = "0";
}
